import { forwardRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

/**
 * InstagramCard — 1080×1350 portrait card designed for Instagram/Threads
 * posts. Renders ENTIRELY with inline styles so html-to-image can
 * rasterize it deterministically (Tailwind class resolution at draw time
 * is unreliable). The component is always rendered offscreen by its
 * parent; visibility is the parent's responsibility.
 *
 * Props:
 *   - data: {
 *       kindBadge:    string  // "DIÁLOGOS DE DIREITO ADMINISTRATIVO" / "ARTIGO DOUTRINÁRIO"
 *       title:        string
 *       author:       string
 *       year?:        string|number
 *       thumbnail?:   string  // optional hero image URL (16:9 ideal for videos, free for articles)
 *       accentColor?: string  // hex; defaults to JurisTube red
 *     }
 *   - qrUrl: string — canonical URL to encode in the QR
 *
 * The thumbnail is loaded with crossOrigin="anonymous" so html-to-image
 * can paint it onto the export canvas. Hosts without CORS (e.g. some
 * scraped covers) will fall back to the placeholder block.
 */
const InstagramCard = forwardRef(function InstagramCard({ data, qrUrl }, ref) {
  const accent = data?.accentColor || "#dc2626"; // red-600
  const title = (data?.title || "").trim();
  const author = (data?.author || "").trim();
  const kindBadge = (data?.kindBadge || "JurisTube").toUpperCase();
  const year = data?.year ? String(data.year) : "";
  const thumb = data?.thumbnail || "";
  // ``mode="author"`` switches the hero from a 16:9 thumbnail strip to
  // a centered circular portrait — required because author photos are
  // square, not 16:9, and stretching them into a thumbnail crop looks
  // unprofessional. Card title becomes the author name in a serif
  // display weight, and the accent shifts to amber so the card visually
  // signals "personal page" rather than "video / article".
  const mode = data?.mode || "media";
  const isAuthorMode = mode === "author";

  // Adaptive title size: shrinks for long titles so we never overflow.
  // Tested against ~30-char to ~140-char Brazilian academic titles.
  const titleLen = title.length;
  const titleSize = isAuthorMode
    ? (titleLen <= 18 ? 110 : titleLen <= 28 ? 92 : titleLen <= 40 ? 74 : 60)
    : (titleLen <= 50 ? 78 : titleLen <= 90 ? 64 : titleLen <= 130 ? 54 : 46);
  const titleLineHeight = titleSize <= 60 ? 1.18 : 1.12;

  return (
    <div
      ref={ref}
      style={{
        width: 1080,
        height: 1350,
        position: "relative",
        background:
          "linear-gradient(180deg, #020617 0%, #0f172a 65%, #1e1b4b 100%)",
        color: "#f1f5f9",
        fontFamily: "'Figtree', system-ui, sans-serif",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Decorative accent bar at the very top */}
      <div
        style={{
          height: 14,
          width: "100%",
          background: `linear-gradient(90deg, ${accent} 0%, #f59e0b 100%)`,
        }}
      />

      {/* Top brand row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "32px 56px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            fontWeight: 700,
            letterSpacing: 1,
            fontSize: 22,
          }}
        >
          <img
            src="/brand/juristube-icon-transparent.svg"
            alt=""
            width={44}
            height={44}
            crossOrigin="anonymous"
            style={{ display: "block" }}
          />
          <span>
            Juris<span style={{ color: accent }}>tube</span>
          </span>
        </div>
        <span
          style={{
            fontSize: 16,
            color: "#94a3b8",
            letterSpacing: 2,
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          juristube.com.br
        </span>
      </div>

      {/* Hero — author mode shows a centered circular portrait,
          everything else uses the 16:9 thumbnail strip. */}
      {isAuthorMode ? (
        <div
          style={{
            margin: "60px 56px 0",
            height: 540,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Soft amber glow halo behind the portrait */}
          <div
            style={{
              position: "absolute",
              width: 540,
              height: 540,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 50% 50%, rgba(245, 158, 11, 0.28) 0%, transparent 65%)",
              filter: "blur(40px)",
            }}
          />
          <div
            style={{
              width: 460,
              height: 460,
              borderRadius: "50%",
              overflow: "hidden",
              border: "8px solid rgba(245, 158, 11, 0.85)",
              boxShadow: "0 32px 80px rgba(0, 0, 0, 0.55), 0 0 0 16px rgba(15, 23, 42, 0.6)",
              position: "relative",
              background:
                "linear-gradient(135deg, #1e293b 0%, #312e81 100%)",
            }}
          >
            {thumb ? (
              <img
                src={thumb}
                alt=""
                crossOrigin="anonymous"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 220,
                  color: "rgba(245, 158, 11, 0.55)",
                  fontWeight: 700,
                }}
              >
                {(title.match(/\b\w/g) || []).slice(0, 2).join("").toUpperCase() || "JT"}
              </div>
            )}
          </div>
          {/* "Página pessoal" badge floats over the portrait bottom */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              background: accent,
              color: "#ffffff",
              padding: "12px 24px",
              borderRadius: 999,
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: 2,
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.45)",
            }}
          >
            {kindBadge}
          </div>
        </div>
      ) : (
        <div
          style={{
            margin: "40px 56px 0",
            height: 540,
            borderRadius: 24,
            overflow: "hidden",
            position: "relative",
            background:
              "linear-gradient(135deg, #1e293b 0%, #312e81 100%)",
            boxShadow: "0 32px 64px rgba(0, 0, 0, 0.45)",
          }}
        >
        {thumb ? (
          <img
            src={thumb}
            alt=""
            crossOrigin="anonymous"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          /* Decorative fallback — large outline quotation mark watermark
              over a subtle radial gradient so the card never looks empty
              when the source has no cover image (typical for legacy
              uploaded PDFs). */
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 30% 30%, rgba(245, 158, 11, 0.25) 0%, transparent 60%), linear-gradient(135deg, #0f172a 0%, #312e81 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 520,
                lineHeight: 0.8,
                color: "rgba(245, 158, 11, 0.18)",
                fontWeight: 900,
                userSelect: "none",
              }}
            >
              &ldquo;
            </span>
            <span
              style={{
                position: "absolute",
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 96,
                lineHeight: 1,
                color: "#f59e0b",
                fontWeight: 700,
                letterSpacing: 12,
                textAlign: "center",
                textTransform: "uppercase",
              }}
            >
              Doutrina
            </span>
          </div>
        )}
        {/* Bottom-up gradient so the kind badge stays readable */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, transparent 55%, rgba(2, 6, 23, 0.85) 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 24,
            bottom: 24,
            background: accent,
            color: "#ffffff",
            padding: "10px 18px",
            borderRadius: 999,
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: 1.5,
          }}
        >
          {kindBadge}
        </div>
      </div>
      )}

      {/* Title + author */}
      <div style={{ padding: "36px 56px 0" }}>
        <p
          role="presentation"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: titleSize,
            lineHeight: titleLineHeight,
            fontWeight: 700,
            margin: 0,
            color: "#ffffff",
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </p>
        {author && (
          <p
            style={{
              marginTop: 18,
              marginBottom: 0,
              fontSize: 24,
              color: "#cbd5e1",
              fontWeight: 500,
              letterSpacing: 0.5,
            }}
          >
            por <span style={{ color: "#f59e0b" }}>{author}</span>
            {year ? `  ·  ${year}` : ""}
          </p>
        )}
      </div>

      {/* Footer: QR + call-to-scan */}
      <div
        style={{
          position: "absolute",
          left: 56,
          right: 56,
          bottom: 56,
          display: "flex",
          alignItems: "center",
          gap: 28,
          padding: 24,
          background: "rgba(15, 23, 42, 0.65)",
          border: "1px solid rgba(245, 158, 11, 0.35)",
          borderRadius: 24,
          backdropFilter: "blur(6px)",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            padding: 14,
            borderRadius: 16,
            display: "flex",
          }}
        >
          <QRCodeCanvas
            value={qrUrl}
            size={180}
            level="M"
            marginSize={0}
            bgColor="#ffffff"
            fgColor="#020617"
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 18,
              color: "#f59e0b",
              letterSpacing: 2,
              textTransform: "uppercase",
              fontWeight: 700,
              marginBottom: 8,
            }}
          >
            Escaneie para acessar
          </div>
          <div
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 30,
              lineHeight: 1.2,
              fontWeight: 700,
              color: "#ffffff",
            }}
          >
            O acervo aberto de Direito brasileiro
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 16,
              color: "#94a3b8",
              fontWeight: 500,
            }}
          >
            curadoria humana · acesso 100% gratuito
          </div>
        </div>
      </div>
    </div>
  );
});

export default InstagramCard;
